import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STORAGE_KEY = 'mb_onboarded';

interface OnboardingSheetProps {
  onComplete: () => void;
  show: boolean;
}

export default function OnboardingSheet({ show, onComplete }: OnboardingSheetProps) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [show]);

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    setTimeout(onComplete, 350);
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    setTimeout(onComplete, 350);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-x-0 bottom-0 z-50 flex justify-center pointer-events-none"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28, duration: 0.4 }}
        >
          <div
            className="w-full max-w-[420px] pointer-events-auto"
            style={{
              background: 'hsl(var(--background) / 0.97)',
              backdropFilter: 'blur(20px)',
              borderTop: '1px solid hsl(var(--foreground) / 0.08)',
              borderRadius: '24px 24px 0 0',
              padding: '24px 20px 36px',
            }}
          >
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mb-5">
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === step ? 10 : 6,
                    height: 6,
                    background: i === step
                      ? 'hsl(var(--bitcoin) / 0.7)'
                      : 'hsl(var(--foreground) / 0.1)',
                    borderRadius: i === step ? 3 : '50%',
                  }}
                />
              ))}
            </div>

            {/* Skip */}
            <button
              onClick={handleSkip}
              className="absolute top-5 right-5 text-[8px] font-body tracking-wider uppercase transition-colors"
              style={{ color: 'hsl(var(--foreground) / 0.2)' }}
            >
              skip
            </button>

            {/* Step content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {step === 0 && <Step1 />}
                {step === 1 && <Step2 />}
                {step === 2 && <Step3 />}
                {step === 3 && <Step4 />}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              {step > 0 ? (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="text-[9px] font-body tracking-wider"
                  style={{ color: 'hsl(var(--foreground) / 0.25)' }}
                >
                  ←
                </button>
              ) : <div />}

              <button
                onClick={() => step < 3 ? setStep(s => s + 1) : handleComplete()}
                className="rounded-xl px-6 py-2.5 text-[11px] font-body font-medium transition-colors"
                style={{
                  background: 'hsl(var(--bitcoin) / 0.15)',
                  border: '1px solid hsl(var(--bitcoin) / 0.25)',
                  color: 'hsl(var(--bitcoin))',
                }}
              >
                {step === 0 ? 'Say hi →' : step === 1 ? 'Got it →' : step === 2 ? 'One more →' : "Let's go ✓"}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Step1() {
  return (
    <div className="text-center">
      <motion.div
        className="text-5xl mb-4"
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        ⛏️
      </motion.div>
      <h2
        className="font-pixel text-[13px] tracking-wider mb-3"
        style={{ color: 'hsl(var(--bitcoin))' }}
      >
        Meet Mempool Buddy
      </h2>
      <p
        className="font-body text-[11px] leading-relaxed mb-2"
        style={{ color: 'hsl(var(--foreground) / 0.7)' }}
      >
        A living on-chain companion that feels the Bitcoin network
        so you don't have to.
      </p>
      <p
        className="font-body text-[9px] leading-relaxed"
        style={{ color: 'hsl(var(--foreground) / 0.35)' }}
      >
        Buddy's mood, expressions, and island environment all reflect
        what's actually happening on-chain — fees, congestion,
        price action, and macro trends.
      </p>
    </div>
  );
}

function Step2() {
  const features = [
    { emoji: '📊', label: 'Live HUD', desc: 'Price, fees, blocks, hashrate & halving countdown' },
    { emoji: '🏝️', label: 'Mood island', desc: 'Weather & lighting shift with the network' },
    { emoji: '📰', label: 'News ticker', desc: 'Scrolling Bitcoin headlines — tap to expand' },
  ];
  return (
    <div className="text-center">
      <div className="flex flex-col gap-2.5 mb-4">
        {features.map(f => (
          <div
            key={f.label}
            className="flex items-center gap-3 rounded-xl px-3 py-2"
            style={{
              background: 'hsl(var(--foreground) / 0.03)',
              border: '1px solid hsl(var(--foreground) / 0.05)',
            }}
          >
            <span className="text-[18px] shrink-0">{f.emoji}</span>
            <div className="text-left min-w-0">
              <span
                className="font-body font-semibold text-[10px] block"
                style={{ color: 'hsl(var(--foreground) / 0.7)' }}
              >
                {f.label}
              </span>
              <span
                className="font-body text-[8px]"
                style={{ color: 'hsl(var(--foreground) / 0.35)' }}
              >
                {f.desc}
              </span>
            </div>
          </div>
        ))}
      </div>
      <h2
        className="font-pixel text-[11px] tracking-wider mb-2"
        style={{ color: 'hsl(var(--bitcoin))' }}
      >
        Everything at a glance
      </h2>
      <p
        className="font-body text-[10px] leading-relaxed"
        style={{ color: 'hsl(var(--foreground) / 0.5)' }}
      >
        Tap the top panel to expand mining stats and halving countdown.
        Tap 📖 for the full Guidance view.
      </p>
    </div>
  );
}

function Step3() {
  const actions = [
    { emoji: '⛏', label: 'Build a Block', desc: 'Fill a 10 000 vB block — no time limit!' },
    { emoji: '🟢', label: 'Matrix mode', desc: 'Red pill activates the green rain overlay' },
    { emoji: '📤', label: 'Share', desc: 'Snapshot Buddy and share with friends' },
  ];
  return (
    <div className="text-center">
      <div className="flex flex-col gap-2.5 mb-4">
        {actions.map(a => (
          <div
            key={a.label}
            className="flex items-center gap-3 rounded-xl px-3 py-2"
            style={{
              background: 'hsl(var(--foreground) / 0.03)',
              border: '1px solid hsl(var(--foreground) / 0.05)',
            }}
          >
            <span className="text-[18px] shrink-0">{a.emoji}</span>
            <div className="text-left min-w-0">
              <span
                className="font-body font-semibold text-[10px] block"
                style={{ color: 'hsl(var(--foreground) / 0.7)' }}
              >
                {a.label}
              </span>
              <span
                className="font-body text-[8px]"
                style={{ color: 'hsl(var(--foreground) / 0.35)' }}
              >
                {a.desc}
              </span>
            </div>
          </div>
        ))}
      </div>
      <h2
        className="font-pixel text-[11px] tracking-wider mb-2"
        style={{ color: 'hsl(var(--bitcoin))' }}
      >
        Play & explore
      </h2>
      <p
        className="font-body text-[10px] leading-relaxed"
        style={{ color: 'hsl(var(--foreground) / 0.5)' }}
      >
        Enter Matrix mode with the red pill, then tap ⛏ Build a Block.
        Difficulty scales with real mempool congestion!
      </p>
    </div>
  );
}

function Step4() {
  const segments = [
    { color: 'hsl(200, 80%, 55%)', label: 'Deep Value' },
    { color: 'hsl(160, 70%, 45%)', label: 'Accumulation' },
    { color: 'hsl(45, 80%, 55%)', label: 'Neutral' },
    { color: 'hsl(30, 85%, 55%)', label: 'Warm' },
    { color: 'hsl(0, 75%, 55%)', label: 'Hot' },
  ];
  return (
    <div className="text-center">
      {/* Mini barometer visual */}
      <div className="flex items-center justify-center mb-3">
        <div
          className="rounded-xl px-4 py-2.5 flex flex-col items-center gap-2"
          style={{
            background: 'hsl(var(--foreground) / 0.03)',
            border: '1px solid hsl(var(--foreground) / 0.05)',
          }}
        >
          <span className="text-[18px]">📐</span>
          <div className="flex gap-0.5">
            {segments.map(s => (
              <div
                key={s.label}
                className="flex flex-col items-center gap-1"
              >
                <div
                  className="rounded-sm"
                  style={{ width: 36, height: 6, background: s.color, opacity: 0.7 }}
                />
                <span
                  className="font-body text-[6px] leading-none"
                  style={{ color: 'hsl(var(--foreground) / 0.35)' }}
                >
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <h2
        className="font-pixel text-[11px] tracking-wider mb-2"
        style={{ color: 'hsl(var(--bitcoin))' }}
      >
        Macro & Feed Buddy
      </h2>
      <p
        className="font-body text-[10px] leading-relaxed mb-2"
        style={{ color: 'hsl(var(--foreground) / 0.7)' }}
      >
        Buddy tracks Bitcoin's 200-week moving average to gauge
        where we are in the cycle — from Deep Value to overheated.
      </p>
      <p
        className="font-body text-[9px] leading-relaxed mb-2"
        style={{ color: 'hsl(var(--foreground) / 0.35)' }}
      >
        Tap Buddy's island to open the Island Barometer —
        a premium gauge with macro insights and trend context.
      </p>

      {/* Feed Buddy info */}
      <div
        className="flex items-center gap-3 rounded-xl px-3 py-2 mt-2"
        style={{
          background: 'hsl(var(--bitcoin) / 0.05)',
          border: '1px solid hsl(var(--bitcoin) / 0.12)',
        }}
      >
        <span className="text-[18px] shrink-0">₿</span>
        <div className="text-left min-w-0">
          <span
            className="font-body font-semibold text-[10px] block"
            style={{ color: 'hsl(var(--bitcoin))' }}
          >
            Feed Buddy
          </span>
          <span
            className="font-body text-[8px]"
            style={{ color: 'hsl(var(--foreground) / 0.4)' }}
          >
            Support the island with Bitcoin — on-chain or ⚡ Lightning. Find the ₿ button at the bottom.
          </span>
        </div>
      </div>
    </div>
  );
}

export function useOnboarding() {
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    setNeedsOnboarding(localStorage.getItem(STORAGE_KEY) !== 'true');
  }, []);

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setNeedsOnboarding(true);
  };

  const complete = () => setNeedsOnboarding(false);

  return { needsOnboarding, reset, complete };
}
